import { useEffect, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import {
  checkResetToken,
  requestPasswordReset,
  resetPassword,
} from '../lib/accountClient.js';

/**
 * Sign in, create an account, and the two halves of a password reset.
 *
 * Four modes in one sheet rather than four routes: the fields are nearly the
 * same each time and the difference is one word on a button, so separate
 * screens would be more navigation than the task deserves. It also means
 * someone who mistypes their password can get to "forgot it" and back without
 * losing the modal they were in.
 *
 * The password rule is a length minimum and nothing else. Composition rules
 * ("one capital, one symbol") push people towards shorter, more predictable
 * passwords, which is the opposite of what they are for.
 */
/**
 * A one-tap way in.
 *
 * Shown above the form rather than below it, because for most people it is the
 * shorter road and burying it under a password field means they never see it.
 * It is a plain link, not a button that calls an API: the whole flow is a pair
 * of redirects the server owns, so there is nothing here to go wrong offline
 * and no third-party script on the page.
 */
function GoogleButton() {
  return (
    <>
      <a className="oauth-btn" href="/api/auth/google">
        <svg className="oauth-btn__mark" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
        </svg>
        Continue with Google
      </a>
      <p className="oauth-or"><span>or</span></p>
    </>
  );
}

export default function AccountModal({
  mode: initialMode = 'signin',
  reason,
  account,
  resetToken,
  onClose,
  onAuthed,
}) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [sent, setSent] = useState(false);
  // null while we ask the server whether the link is still good.
  const [tokenState, setTokenState] = useState(resetToken ? null : 'n/a');
  const firstFieldRef = useRef(null);
  /**
   * Whether the deployment can actually do this.
   *
   * Defaults to hidden. A "Continue with Google" button on a server without the
   * credentials sends somebody out to Google and lands them back on an error —
   * the worst possible moment to discover a setting is missing.
   */
  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    fetch('/api/health?json', { credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : null))
      .then((health) => {
        if (live && health?.google) setGoogleEnabled(true);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const isSignUp = mode === 'signup';
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Check the link before asking for a new password. Finding out it expired
  // *after* typing one is a small thing that feels like a large one.
  useEffect(() => {
    if (!resetToken) return undefined;
    let live = true;
    checkResetToken(resetToken)
      .then((result) => {
        if (!live) return;
        setTokenState(result.valid ? 'valid' : 'expired');
        if (result.email) setEmail(result.email);
      })
      .catch(() => live && setTokenState('expired'));
    return () => {
      live = false;
    };
  }, [resetToken]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      if (isForgot) {
        await requestPasswordReset(email.trim());
        setSent(true);
      } else if (isReset) {
        await account.applyReset(resetToken, password);
        onClose();
      } else if (isSignUp) {
        await account.signUp(email.trim(), password);
        onClose();
        // Whatever they were part-way through doing when this opened. Signing
        // up is almost never the errand itself — it is the step in front of
        // one — and dropping somebody in the app having forgotten what they
        // pressed is how a funnel loses people at the last moment.
        onAuthed?.();
      } else {
        await account.signIn(email.trim(), password);
        onClose();
        onAuthed?.();
      }
    } catch (error) {
      setProblem(error.message);
      // An existing address on the sign-up form is nearly always someone who
      // forgot they had an account — put them on the right form rather than
      // making them find the switch.
      if (error.code === 'exists') setMode('signin');
    } finally {
      setBusy(false);
    }
  };

  const title = isReset
    ? 'Choose a new password'
    : isForgot
      ? 'Reset your password'
      : isSignUp
        ? 'Create an account'
        : 'Welcome back';

  const lead = isReset
    ? 'This signs you in and signs out every other device.'
    : isForgot
      ? 'Enter your address and we’ll send you a link.'
      : (reason ??
        (isSignUp
          ? 'An account keeps your streak and progress on every device you use.'
          : 'Sign in to pick up where you left off.'));

  const action = isReset
    ? 'Set password and sign in'
    : isForgot
      ? 'Send the link'
      : isSignUp
        ? 'Create account'
        : 'Sign in';

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="account-title">
      <button className="modal__scrim" onClick={onClose} aria-label="Close" tabIndex={-1} />

      <div className="modal__sheet modal__sheet--account">
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={14} />
        </button>

        <span className="modal__glyph">
          <Icon name="cross" size={24} />
        </span>

        <h2 id="account-title" className="modal__title">
          {tokenState === 'expired' ? 'That link has expired' : title}
        </h2>
        <p className="modal__sub">
          {tokenState === 'expired'
            ? 'Reset links last an hour and work once. Ask for a fresh one.'
            : lead}
        </p>

        {/* An expired link is a dead end, so offer the only useful way out. */}
        {tokenState === 'expired' ? (
          <button
            type="button"
            className="btn btn--primary btn--block btn--lg"
            onClick={() => {
              setTokenState('n/a');
              setMode('forgot');
              setPassword('');
            }}
          >
            Send a new link
          </button>
        ) : sent ? (
          <>
            <p className="account-form__sent" role="status">
              If <strong>{email.trim()}</strong> has an account, a reset link is on its way.
              It works once and expires in an hour.
            </p>
            <button type="button" className="btn btn--ghost btn--block" onClick={onClose}>
              Done
            </button>
          </>
        ) : tokenState === null ? (
          <p className="account-form__sent">Checking that link…</p>
        ) : (
          <>
            {googleEnabled && !isReset && !isForgot ? <GoogleButton /> : null}

            <form className="account-form" onSubmit={submit}>
              {/* On a reset the address is fixed by the token, so it is shown
                  rather than asked for — and kept in the DOM for password
                  managers, which need it to save against the right account. */}
              {isReset ? (
                <>
                  <p className="account-form__whom">{email}</p>
                  <input type="email" value={email} readOnly hidden autoComplete="username" />
                </>
              ) : (
                <label className="field">
                  <span>Email</span>
                  <input
                    ref={isReset ? null : firstFieldRef}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </label>
              )}

              {isForgot ? null : (
                <label className="field">
                  <span>{isReset ? 'New password' : 'Password'}</span>
                  <input
                    ref={isReset ? firstFieldRef : null}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete={isSignUp || isReset ? 'new-password' : 'current-password'}
                    minLength={isSignUp || isReset ? 10 : undefined}
                    required
                  />
                  {isSignUp || isReset ? (
                    <small>At least 10 characters. A short phrase beats a short password.</small>
                  ) : null}
                </label>
              )}

              {problem ? (
                <p className="account-form__problem" role="alert">
                  {problem}
                </p>
              ) : null}

              <button
                type="submit"
                className="btn btn--primary btn--block btn--lg"
                disabled={busy}
              >
                {busy ? 'One moment…' : action}
              </button>
            </form>

            {isReset ? null : (
              <p className="account-form__switch">
                {isForgot ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signin');
                      setProblem(null);
                    }}
                  >
                    Back to sign in
                  </button>
                ) : (
                  <>
                    {isSignUp ? 'Already have an account?' : 'No account yet?'}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setMode(isSignUp ? 'signin' : 'signup');
                        setProblem(null);
                      }}
                    >
                      {isSignUp ? 'Sign in' : 'Create one'}
                    </button>
                    {isSignUp ? null : (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={() => {
                            setMode('forgot');
                            setProblem(null);
                          }}
                        >
                          Forgot password?
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
